import { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import Select from 'react-select'

import API from '../../../../api/api'
import { useSnackbar } from '../../../../helpers/hooks/useSnackbar'
import { saltoSpaceDropdownStyles } from '../helpers'
import Loader from '../../../Loader/Loader'
import ConfirmPopup from '../../../Popups/ConfirmPopup'

// Temporarily hidden in the release - flip to false to bring the button back.
const IS_CONNECT_EXISTING_HIDDEN = true

const AssaAbloyVisionLineSection = ({ isAvailable, clientId, buildingId, apartment }) => {
  const { displaySnackbar } = useSnackbar()

  const [isLoading, setIsLoading] = useState(true)
  const [isUserConnected, setIsUserConnected] = useState(false)
  const [isCreatingUser, setIsCreatingUser] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [isConfirming, setIsConfirming] = useState(false)
  const [showConnectForm, setShowConnectForm] = useState(false)
  const [showEntranceForm, setShowEntranceForm] = useState(false)
  const [entrances, setEntrances] = useState([])
  const [selectedEntranceIds, setSelectedEntranceIds] = useState([])
  const [disconnectedUsers, setDisconnectedUsers] = useState([])
  const [selectedUserId, setSelectedUserId] = useState(null)
  const [isRevokePopupVisible, setIsRevokePopupVisible] = useState(false)
  const [isRevoking, setIsRevoking] = useState(false)

  useEffect(() => {
    if (!isAvailable || !clientId) {
      setIsLoading(false)
      return
    }
    setIsLoading(true)
    API.isVisionLineUserConnected(clientId)
      .then(({ data }) => setIsUserConnected(data))
      .catch(() => setIsUserConnected(false))
      .finally(() => setIsLoading(false))
  }, [isAvailable, clientId])

  const getAssignedEntranceIdsFromUser = (data) => {
    const allEntrances = data.entrances || []
    const assigned = allEntrances.filter((e) => e.isAssigned || e.assigned || e.granted || e.active)
    const source = assigned.length > 0 ? assigned : allEntrances
    return source.map((e) => e.id).filter(Boolean)
  }

  useEffect(() => {
    if (!isUserConnected || !clientId || !buildingId) return
    Promise.all([API.getVisionLineEntrances(buildingId), API.getVisionLineUser(clientId)])
      .then(([entrancesRes, userRes]) => {
        setEntrances(entrancesRes.data || [])
        setSelectedEntranceIds(getAssignedEntranceIdsFromUser(userRes.data))
        setShowEntranceForm(true)
      })
      .catch(() => setShowEntranceForm(true))
  }, [isUserConnected, clientId, buildingId])

  const handleCreateNewUser = async () => {
    try {
      setIsCreatingUser(true)

      await API.createVisionLineUser(clientId)

      const { data: fetchedEntrances } = await API.getVisionLineEntrances(buildingId)
      const matchedEntranceIds = (fetchedEntrances || [])
        .filter((e) => e.name === String(apartment))
        .map((e) => e.id)
      await API.updateVisionLineUser(clientId, matchedEntranceIds)

      const { data: userData } = await API.getVisionLineUser(clientId)

      setEntrances(fetchedEntrances || [])
      setSelectedEntranceIds(getAssignedEntranceIdsFromUser(userData))
      setIsUserConnected(true)
      setShowEntranceForm(true)

      displaySnackbar('VisionLine user has been successfully created')
    } catch {
      displaySnackbar('Error occurred creating VisionLine user. Please try again', true)
    } finally {
      setIsCreatingUser(false)
    }
  }

  const handleConnectExistingAccount = async () => {
    try {
      const { data } = await API.getDisconnectedVisionLineUsers(buildingId)

      setDisconnectedUsers(data)
      setShowConnectForm(true)
    } catch {
      displaySnackbar(
        'We were not able to load the VisionLine accounts available to connect. Please try again later.',
        true,
      )
    }
  }

  const handleConnectUser = async () => {
    try {
      setIsConnecting(true)

      await API.connectVisionLineUser(clientId, selectedUserId)

      const [entrancesRes, userRes] = await Promise.all([
        API.getVisionLineEntrances(buildingId),
        API.getVisionLineUser(clientId),
      ])

      setEntrances(entrancesRes.data || [])
      setSelectedEntranceIds(getAssignedEntranceIdsFromUser(userRes.data))
      setIsUserConnected(true)
      setShowConnectForm(false)
      setShowEntranceForm(true)

      displaySnackbar('VisionLine user has been successfully connected')
    } catch {
      displaySnackbar('Error occurred connecting VisionLine user. Please try again', true)
    } finally {
      setIsConnecting(false)
    }
  }

  const handleConfirmEntrances = async () => {
    try {
      setIsConfirming(true)
      await API.updateVisionLineUser(clientId, selectedEntranceIds)

      try {
        const { data } = await API.getVisionLineUser(clientId)
        const grantedIds = getAssignedEntranceIdsFromUser(data)
        if (grantedIds.length > 0 || selectedEntranceIds.length === 0) {
          setSelectedEntranceIds(grantedIds)
        }
      } catch {
        setShowEntranceForm(true)
      }

      displaySnackbar('Access has been successfully granted')
    } catch {
      displaySnackbar('Error occurred granting the access. Please try again', true)
    } finally {
      setIsConfirming(false)
    }
  }

  const handleRevoke = async () => {
    try {
      setIsRevoking(true)
      await API.revokeVisionLineEndpoint(clientId)

      setIsUserConnected(false)
      setShowEntranceForm(false)
      setShowConnectForm(false)
      setEntrances([])
      setSelectedEntranceIds([])

      displaySnackbar("Resident's device and access have been revoked")
    } catch (error) {
      const status = error?.response?.status
      if (status === 424) {
        displaySnackbar('We were not able to revoke access. Please try again later.', true)
      } else if (status === 503) {
        // The keys are already off the doors, only the VisionLine container survived - the button
        // stays so the property manager can run it again to finish the job.
        displaySnackbar(
          'We were not able to revoke the Visionline connection. Please try again later.',
          true,
        )
      } else {
        displaySnackbar('Error occurred revoking the device. Please try again', true)
      }
    } finally {
      setIsRevoking(false)
      setIsRevokePopupVisible(false)
    }
  }

  const onChangeEntrances = (selectedOptions) => {
    const entranceIds = selectedOptions ? selectedOptions.map((option) => option.id) : []
    setSelectedEntranceIds(entranceIds)
  }

  const getSelectedEntrancesValue = () => {
    const selectedValues = selectedEntranceIds
      .map((id) => entrances.find((entrance) => entrance.id === id))
      .filter(Boolean)
    return selectedValues
  }

  const onChangeUser = (selectedOption) => {
    setSelectedUserId(selectedOption ? selectedOption.id : null)
  }

  const getSelectedUserValue = () => {
    return disconnectedUsers.find((user) => user.id === selectedUserId) || null
  }

  if (!isAvailable) return null

  if (isLoading)
    return (
      <div className="information-element">
        <div className="information-element-row">
          <Loader />
        </div>
      </div>
    )

  return (
    <div className="information-element information-element-column">
      {showEntranceForm ? (
        <div className="information-element-row">
          <div className="element-name">AssaAbloy VisionLine</div>
          <div className="columns" style={{ width: '100%' }}>
            <div className="column" style={{ width: '100%' }}>
              <div className="dropdown-container" style={{ flex: 1, minWidth: 0 }}>
                <Select
                  styles={saltoSpaceDropdownStyles()}
                  classNamePrefix="dropdown-button"
                  options={entrances}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id}
                  value={getSelectedEntrancesValue()}
                  onChange={onChangeEntrances}
                  placeholder="Select Door..."
                  isClearable
                  isMulti
                  closeMenuOnSelect={false}
                />
              </div>
              <button
                type="button"
                className={`button button-primary ${isConfirming ? 'is-loading' : ''}`}
                style={{ flex: 1 }}
                disabled={isConfirming}
                onClick={handleConfirmEntrances}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      ) : showConnectForm ? (
        <div className="information-element-row">
          <div className="element-name">AssaAbloy VisionLine</div>
          <div className="columns" style={{ width: '100%' }}>
            <div className="column" style={{ width: '100%' }}>
              <div className="dropdown-container" style={{ flex: 1, minWidth: 0 }}>
                <Select
                  styles={saltoSpaceDropdownStyles()}
                  classNamePrefix="dropdown-button"
                  options={disconnectedUsers}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.id}
                  value={getSelectedUserValue()}
                  onChange={onChangeUser}
                  placeholder="Select User..."
                  isClearable
                />
              </div>
              <button
                type="button"
                className={`button button-primary ${isConnecting ? 'is-loading' : ''}`}
                style={{ flex: 1 }}
                disabled={isConnecting || !selectedUserId}
                onClick={handleConnectUser}
              >
                {isConnecting ? 'Connecting...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      ) : !isUserConnected ? (
        <div className="information-element-row">
          <div className="element-name">AssaAbloy VisionLine</div>
          <div className="columns">
            <div className="column column-right">
              <button
                className={`button button-primary ${isCreatingUser ? 'is-loading' : ''}`}
                onClick={handleCreateNewUser}
                disabled={isCreatingUser}
              >
                {isCreatingUser ? 'Creating...' : 'Create New User'}
              </button>
              {!IS_CONNECT_EXISTING_HIDDEN && (
                <>
                  <span>or</span>
                  <button
                    className="button button-primary"
                    onClick={handleConnectExistingAccount}
                    disabled={isCreatingUser}
                  >
                    Connect Existing Account
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="information-element-row">
          <Loader />
        </div>
      )}

      {showEntranceForm && (
        <button
          type="button"
          className={`disconnect-profile-button button-primary button ${
            isRevoking ? 'is-loading' : ''
          }`}
          disabled={isRevoking}
          onClick={() => setIsRevokePopupVisible(true)}
        >
          Revoke Device&amp;Access
        </button>
      )}

      <ConfirmPopup
        title="Confirmation"
        body="Are you sure that you want to revoke the resident's device and all access? The resident won't be able to use the keys on the device and will need to activate a new one to regain access."
        cancelText="Cancel"
        confirmText="Yes, revoke access"
        isOpen={isRevokePopupVisible}
        cancelCallback={() => setIsRevokePopupVisible(false)}
        confirmCallback={handleRevoke}
        isLoading={isRevoking}
      />
    </div>
  )
}

AssaAbloyVisionLineSection.propTypes = {
  isAvailable: PropTypes.bool,
  isUserConnected: PropTypes.bool,
  clientId: PropTypes.number,
  buildingId: PropTypes.number,
  apartment: PropTypes.number,
}

export default AssaAbloyVisionLineSection
